import { useCallback, useMemo } from 'react';
import { FieldValues, useFormContext, useWatch } from 'react-hook-form';
import {
  LabelType,
  LabelValueSource,
  LabelValueAcceptedBy
} from 'graphql/types';

import { useFeatureFlag } from 'modules/common/hooks';
import { FormTimesheetLabel } from '../labelSchema';
import { isLabelValueEmpty } from './utils';

type useBulkLabelSuggestionsActionsResult = {
  acceptAllLabelSuggestionHandler: () => void;
  denyAllLabelSuggestionHandler: () => void;
  enableTimesheetActions: boolean;
};
type useBulkLabelSuggestionsActionsProps = {
  fieldName: string;
};
export const useBulkLabelSuggestionsActions = ({
  fieldName
}: useBulkLabelSuggestionsActionsProps): useBulkLabelSuggestionsActionsResult => {
  const { setValue, control } = useFormContext();

  const fields = useWatch({
    name: `${fieldName}`,
    control
  });
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const enableTimesheetActions = useMemo(
    () =>
      (fields ?? []).some((field: FormTimesheetLabel) => {
        const value = isTimeSignalFederatedSuggestionsEnabled
          ? field?.label?.suggestedValues?.[0]
          : field?.label?.value;
        return (
          field?.label?.valueSource === LabelValueSource.Suggestion &&
          field?.label?.type !== LabelType.Comment &&
          !isLabelValueEmpty(value) &&
          (isLabelLevelAcceptedByEnabled ? !field?.label?.acceptedBy : true)
        );
      }),
    [
      fields,
      isLabelLevelAcceptedByEnabled,
      isTimeSignalFederatedSuggestionsEnabled
    ]
  );
  const setLabelValue = useCallback(
    (name: string, value: FieldValues) => {
      setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [setValue]
  );
  const acceptAllLabelSuggestionHandler = useCallback(() => {
    (fields ?? []).forEach((field: FormTimesheetLabel, index: number) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.label?.suggestedValues?.[0]
        : field?.label?.value;
      if (
        field?.label?.valueSource === LabelValueSource.Suggestion &&
        value &&
        (isLabelLevelAcceptedByEnabled ? !field?.label?.acceptedBy : true)
      ) {
        setLabelValue(`${fieldName}.${index}.label`, {
          ...field.label,
          valueSource: LabelValueSource.User,
          ...(isLabelLevelAcceptedByEnabled
            ? {
                acceptedBy:
                  field?.label?.acceptedBy ?? LabelValueAcceptedBy.User,
                valueSource: field?.label?.valueSource ?? LabelValueSource.User
              }
            : {}),
          ...(isTimeSignalFederatedSuggestionsEnabled
            ? {
                value: field?.label?.suggestedValues?.[0]
              }
            : {})
        });
      }
    });
  }, [
    fields,
    isLabelLevelAcceptedByEnabled,
    isTimeSignalFederatedSuggestionsEnabled,
    setLabelValue,
    fieldName
  ]);

  const denyAllLabelSuggestionHandler = useCallback(() => {
    (fields ?? []).forEach((field: FormTimesheetLabel, index: number) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.label?.suggestedValues?.[0]
        : field?.label?.value;
      if (
        field?.label?.valueSource === LabelValueSource.Suggestion &&
        value &&
        (isLabelLevelAcceptedByEnabled ? !field?.label?.acceptedBy : true)
      ) {
        setLabelValue(`${fieldName}.${index}.label`, {
          ...field.label,
          value: null,
          ...(isTimeSignalFederatedSuggestionsEnabled
            ? {
                suggestedValues: [],
                suggestionMetadata: null,
                thresholdMet: false
              }
            : {}),
          valueSource: LabelValueSource.Suggestion
        });
      }
    });
  }, [
    fields,
    isTimeSignalFederatedSuggestionsEnabled,
    isLabelLevelAcceptedByEnabled,
    setLabelValue,
    fieldName
  ]);

  return {
    enableTimesheetActions,
    acceptAllLabelSuggestionHandler,
    denyAllLabelSuggestionHandler
  };
};
